<template>
  <b-col md="12" class="mb-3">
    <b-card class="formCardDash">
      <div class="card-header">
        <h6 class="card-title mb-0">Quotations</h6>
        <small class="text-muted">Total Amount: {{ formatCurrency(totalQuotationAmount) }} | Total Records: {{ totalRecords }}</small>
      </div>
      <div class="card-body">
        <!-- Filters -->
        <b-row class="mb-3">
          <b-col md="3">
            <b-form-input
              v-model="searchQuery"
              placeholder="Search by quotation no, client name..."
              @input="debounceSearch"
              size="sm"
            />
          </b-col>
          <b-col md="2">
            <b-form-select
              v-model="perPage"
              :options="[10, 25, 50, 100]"
              @change="updatePagination"
              size="sm"
            />
          </b-col>
          <b-col md="2">
            <b-button
              variant="outline-info"
              size="sm"
              @click="refreshData"
              :disabled="isLoading"
            >
              <i class="fa fa-refresh" :class="{ 'fa-spin': isLoading }"></i>
              Refresh
            </b-button>
          </b-col>
        </b-row>

        <!-- Bootstrap Table with Pagination -->
        <b-table
          :items="quotationsData"
          :fields="fields"
          :busy="isLoading"
          striped
          bordered
          hover
          small
          show-empty
          empty-text="No quotation data found"
          class="mb-0"
        >
          <template slot="table-busy">
            <div class="text-center text-danger my-2">
              <b-spinner class="align-middle" small></b-spinner>
              <strong class="ml-2">Loading...</strong>
            </div>
          </template>

          <template slot="quotationNumber" slot-scope="data">
            <code>{{ data.value }}</code>
          </template>

          <template slot="createdAt" slot-scope="data">
            {{ formatDate(data.value) }}
          </template>

          <template slot="quotationType" slot-scope="data">
            <b-badge :variant="getQuotationTypeVariant(data.value)">{{ data.value }}</b-badge>
          </template>

          <template slot="status" slot-scope="data">
            <b-badge :variant="getStatusVariant(data.value)">{{ getStatusText(data.value) }}</b-badge>
          </template>

          <template slot="clientName" slot-scope="data">
            <strong>{{ data.value || 'Unknown' }}</strong>
            <br>
            <small class="text-muted">{{ data.item.EnqNo || '-' }}</small>
          </template>

          <template slot="POL" slot-scope="data">
            <small>{{ data.value }} → {{ data.item.POD }}</small>
          </template>

          <template slot="totalAmount" slot-scope="data">
            <strong class="text-right d-block">{{ formatCurrency(data.value) }}</strong>
          </template>

          <template slot="actions" slot-scope="data">
            <b-button
              size="sm"
              variant="outline-info"
              @click.stop="toggleDetails(data.item)"
              class="toggle-btn"
            >
              <i :class="data.item._showDetails ? 'fa fa-chevron-up' : 'fa fa-chevron-down'"></i>
            </b-button>
          </template>

          <!-- Row Details Slot -->
          <template slot="row-details" slot-scope="row">
            <div class="row-details">
              <!-- Quotation Details -->
              <div class="row mb-3">
                <div class="col-12">
                  <div class="card border-primary">
                    <div class="card-header bg-primary text-white">
                      <i class="fa fa-file-text"></i> Quotation Details
                    </div>
                    <div class="card-body">
                      <div class="row">
                        <div class="col-md-3">
                          <strong>Quotation No:</strong> {{ row.item.quotationNumber }}
                        </div>
                        <div class="col-md-3">
                          <strong>Type:</strong> {{ row.item.quotationType }}
                        </div>
                        <div class="col-md-3">
                          <strong>Status:</strong> {{ getStatusText(row.item.status) }}
                        </div>
                        <div class="col-md-3">
                          <strong>Created Date:</strong> {{ formatDate(row.item.createdAt) }}
                        </div>
                      </div>
                      <div class="row mt-2">
                        <div class="col-md-3">
                          <strong>Buying Rate:</strong> {{ formatCurrency(row.item.buyingRate) }}
                        </div>
                        <div class="col-md-3">
                          <strong>Selling Rate:</strong> {{ formatCurrency(row.item.sellingRate) }}
                        </div>
                        <div class="col-md-3">
                          <strong>Total Amount:</strong> {{ formatCurrency(row.item.totalAmount) }}
                        </div>
                        <div class="col-md-3" v-if="row.item.ffType">
                          <strong>FF Type:</strong> {{ row.item.ffType }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Location Details -->
              <div class="row mb-3">
                <div class="col-12">
                  <div class="card border-success">
                    <div class="card-header bg-success text-white">
                      <i class="fa fa-map-marker"></i> Location Details
                    </div>
                    <div class="card-body">
                      <div class="row">
                        <div class="col-md-2">
                          <strong>POR:</strong> {{ row.item.POR || row.item.porLocation || '-' }}
                        </div>
                        <div class="col-md-2">
                          <strong>POL:</strong> {{ row.item.POL || row.item.polLocation || '-' }}
                        </div>
                        <div class="col-md-2">
                          <strong>POD:</strong> {{ row.item.POD || row.item.podLocation || '-' }}
                        </div>
                        <div class="col-md-2">
                          <strong>FPOD:</strong> {{ row.item.FPOD || '-' }}
                        </div>
                        <div class="col-md-2">
                          <strong>Stuffing:</strong> {{ row.item.STUFF || '-' }}
                        </div>
                        <div class="col-md-2">
                          <strong>Handover:</strong> {{ row.item.HAND || '-' }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Client & Enquiry Details -->
              <div class="row mb-3">
                <div class="col-12">
                  <div class="card border-warning">
                    <div class="card-header bg-warning">
                      <i class="fa fa-users"></i> Client & Enquiry Details
                    </div>
                    <div class="card-body">
                      <div class="row">
                        <div class="col-md-3">
                          <strong>Main Client:</strong> {{ row.item.ClientName || row.item.clientName || 'Unknown' }}
                        </div>
                        <div class="col-md-3">
                          <strong>Exporter:</strong> {{ row.item.ExpClientName || row.item.exporterClientName || '-' }}
                        </div>
                        <div class="col-md-3">
                          <strong>Importer:</strong> {{ row.item.ImpClientName || row.item.importerClientName || '-' }}
                        </div>
                        <div class="col-md-3">
                          <strong>Enquiry:</strong> {{ row.item.EnqNo || row.item.enquirySeries || '-' }}
                        </div>
                      </div>
                      <div class="row mt-2">
                        <div class="col-md-3">
                          <strong>Enquiry Date:</strong> {{ formatDate(row.item.EnqDate || row.item.enquiryDate) }}
                        </div>
                        <div class="col-md-3">
                          <strong>Job Type:</strong> {{ row.item.jobType || '-' }}
                        </div>
                        <div class="col-md-3">
                          <strong>Transport Type:</strong> {{ row.item.transportType || '-' }}
                        </div>
                        <div class="col-md-3">
                          <strong>Shipping Line:</strong> {{ row.item.SlineName || row.item.shippingLine || '-' }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Quotation Series -->
              <div class="row mb-3">
                <div class="col-12">
                  <div class="card border-info">
                    <div class="card-header bg-info text-white">
                      <i class="fa fa-tags"></i> Quotation Series
                    </div>
                    <div class="card-body">
                      <div class="row">
                        <div class="col-md-3">
                          <strong>Regular:</strong> {{ row.item.quotationSeries || '-' }}
                        </div>
                        <div class="col-md-3">
                          <strong>CC:</strong> {{ row.item.ccQSeries || '-' }}
                        </div>
                        <div class="col-md-3">
                          <strong>FF:</strong> {{ row.item.ffQSeries || '-' }}
                        </div>
                        <div class="col-md-3">
                          <strong>TPT:</strong> {{ row.item.tptQSeries || '-' }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </b-table>

        <!-- Pagination -->
        <b-pagination
          v-if="totalItems > pagination.perPage"
          limit="25"
          align="center"
          v-model="pagination.currentPage"
          :per-page="pagination.perPage"
          :total-rows="totalItems"
          @change="onPageChange"
          class="mt-3"
        ></b-pagination>

        <!-- Table Info -->
        <div class="mt-2 d-flex justify-content-between align-items-center">
          <div>
            <b-form-select
              v-model="pagination.perPage"
              :options="pagination.pageOptions"
              @change="updatePagination"
              size="sm"
              class="w-auto"
            ></b-form-select>
            <span class="ml-2 text-muted">per page</span>
          </div>

          <div class="text-muted small">
            Total Records: {{ totalItems }} | Total Amount: {{ formatCurrency(totalQuotationAmount) }}
          </div>
        </div>

      </div>
    </b-card>
  </b-col>
</template>

<script>
import moment from "moment";
import SalesPersonTeusService from "../../../services/SalesPersonTeusService";

export default {
  name: "QuotationsSection",
  props: {
    quotationsData: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  data() {
    return {
      searchQuery: '',
      searchTimeout: null,
      isLoading: false,
      totalItems: 0,
      totalPages: 1,
      filter: null,
      pagination: {
        currentPage: 1,
        perPage: 10,
        pageOptions: [10, 25, 50, 100],
      },
      fields: [
        { key: 'quotationNumber', label: 'Quotation No', sortable: true },
        { key: 'createdAt', label: 'Created Date', sortable: true },
        { key: 'quotationType', label: 'Type', sortable: false },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'clientName', label: 'Client Name', sortable: true },
        { key: 'POL', label: 'POL → POD', sortable: false },
        { key: 'totalAmount', label: 'Amount', sortable: true, class: 'text-right' },
        { key: 'actions', label: 'Actions', sortable: false, class: 'text-center' }
      ],
    };
  },
  computed: {
    totalQuotationAmount() {
      return this.quotationsData.reduce((sum, quotation) => sum + (parseFloat(quotation.totalAmount) || 0), 0);
    },

    totalRecords() {
      return this.quotationsData.length;
    },
  },
  methods: {
    formatCurrency(value) {
      if (!value && value !== 0) return "₹0";
      return "₹" + Number(value).toLocaleString("en-IN");
    },

    formatDate(date) {
      if (!date) return "-";
      return moment(date).format("DD-MM-YYYY");
    },

    getQuotationTypeVariant(type) {
      const variants = {
        'Regular': 'primary',
        'CC': 'success',
        'FF': 'info',
        'TPT': 'warning'
      };
      return variants[type] || 'secondary';
    },

    getStatusVariant(status) {
      const variants = {
        '1': 'success',
        'Approved': 'success',
        'Pending': 'warning',
        'Rejected': 'danger'
      };
      return variants[status] || 'secondary';
    },

    getStatusText(status) {
      const statusMap = {
        '1': 'Approved',
        'Approved': 'Approved',
        'Pending': 'Pending',
        'Rejected': 'Rejected'
      };
      return statusMap[status] || status;
    },

    debounceSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.filter = this.searchQuery;
      }, 500);
    },

    updatePagination() {
      this.pagination.currentPage = 1;
    },

    onFiltered(filteredItems) {
      // Trigger pagination reset when filtered
      this.pagination.currentPage = 1;
    },

    onPageChange(page) {
      this.pagination.currentPage = page;
      this.loadData();
    },

    toggleDetails(quotation) {
      // Toggle the _showDetails property for BootstrapVue row-details
      quotation._showDetails = !quotation._showDetails;
      this.$forceUpdate(); // Force update to trigger reactivity
    },

    async loadData() {
      this.isLoading = true;
      try {
        // For b-table with client-side pagination, we can load all data at once
        // or implement server-side pagination if needed
        const payload = {
          salesPersonId: this.$store.state.user.userId,
          clientId: this.$store.state.client.clientId || null,
          dateRange: this.$store.state.dateRange || '2025-2026',
          page: this.pagination.currentPage,
          limit: this.pagination.perPage,
          search: this.searchQuery
        };

        const response = await SalesPersonTeusService.getQuotationsData(payload);

        if (response.status) {
          // Initialize _showDetails property for each item
          const data = response.data.quotations.map(item => {
            const newItem = Object.assign({}, item);
            newItem._showDetails = false;
            return newItem;
          });

          this.$emit('data-loaded', {
            data: data,
            pagination: response.data.pagination,
            summary: response.data.summary
          });
          this.totalItems = response.data.pagination.totalItems;
          this.totalPages = response.data.pagination.totalPages;
        }
      } catch (error) {
        console.error('Error loading quotations data:', error);
        this.$emit('data-loaded', {
          data: [],
          pagination: { currentPage: 1, totalPages: 1, totalItems: 0, itemsPerPage: this.pagination.perPage },
          summary: { totalQuotationAmount: 0, totalRecords: 0 }
        });
        this.totalItems = 0;
      } finally {
        this.isLoading = false;
      }
    },

    refreshData() {
      this.loadData();
    },
  },

  mounted() {
    this.loadData();
  },

  watch: {
    quotationsData: {
      handler() {
        // Reset pagination when data changes
        this.pagination.currentPage = 1;
      },
      deep: true
    }
  }
};
</script>

<style scoped>
.formCardDash {
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.expanded-details {
  background-color: #f8f9fa;
  border-top: 2px solid #dee2e6;
  padding: 15px;
}

.expanded-details .row {
  margin-bottom: 10px;
}

.expanded-details h6 {
  margin-bottom: 10px;
  font-weight: 600;
}

.expanded-details table.table-sm {
  font-size: 12px;
}

.expanded-details table.table-sm td {
  padding: 4px 8px;
  border: none;
}

.table-responsive {
  max-height: 600px;
  overflow-y: auto;
}

.table th {
  position: sticky;
  top: 0;
  background-color: #007bff !important;
  color: white !important;
  z-index: 10;
  border-color: #0056b3;
  font-weight: 600;
  text-align: center;
  vertical-align: middle;
  padding: 12px 8px;
}

.table thead th {
  border-bottom: 2px solid #0056b3;
}

.table tbody td {
  vertical-align: middle;
  padding: 8px;
}

.table tbody td.text-center {
  text-align: center;
}

.table tbody td.text-right {
  text-align: right;
}

.badge {
  font-size: 11px;
}

.btn-outline-info {
  border-color: #17a2b8;
  color: #17a2b8;
}

.btn-outline-info:hover {
  background-color: #17a2b8;
  border-color: #17a2b8;
}

.toggle-btn {
  min-width: 40px;
  transition: all 0.2s ease;
}

.toggle-btn:hover {
  transform: scale(1.1);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .expanded-details .col-md-6 {
    margin-bottom: 20px;
  }

  .table-responsive {
    max-height: 400px;
  }
}
</style>



// WEBPACK FOOTER //
// QuotationsSection.vue?021df248